import { X } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type ChipTone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";
type ChipSize = "sm" | "md";

type ChipProps = {
  children: ReactNode;
  tone?: ChipTone;
  size?: ChipSize;
  icon?: ReactNode;
  className?: string;
  onDismiss?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  dismissLabel?: string;
};

const toneClass: Record<ChipTone, string> = {
  neutral: "border-transparent bg-surface-muted text-text-muted",
  accent: "border-transparent bg-accent-50 text-accent-800",
  success: "border-transparent bg-success-bg text-success",
  warning: "border-transparent bg-warning-bg text-warning",
  danger: "border-transparent bg-danger-bg text-danger",
  info: "border-transparent bg-info-bg text-info",
};

const sizeClass: Record<ChipSize, string> = {
  sm: "min-h-6 px-2.5 py-0.5 text-xs",
  md: "min-h-7 px-3 py-1 text-sm",
};

export function Chip({
  children,
  tone = "neutral",
  size = "sm",
  icon,
  className = "",
  onDismiss,
  dismissLabel = "Remove",
}: ChipProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-pill border font-semibold ${toneClass[tone]} ${sizeClass[size]} ${className}`}
    >
      {icon}
      <span className="min-w-0">{children}</span>
      {onDismiss ? (
        <button
          aria-label={dismissLabel}
          className="-mr-1 inline-flex h-5 w-5 items-center justify-center rounded-pill text-current transition hover:bg-black/5 focus-visible:outline-none"
          type="button"
          onClick={onDismiss}
        >
          <X aria-hidden="true" className="h-3 w-3" strokeWidth={1.7} />
        </button>
      ) : null}
    </span>
  );
}
